import { useEffect, useRef } from "react";
import * as THREE from "three";

/** Three.js scene viewer for Generative Design 3D models.
 * Reads scene JSON in the format produced by /app/backend/generative_3d.py
 * and renders it with orbit controls (basic mouse-drag rotation + wheel-zoom).
 */
export default function Scene3DViewer({ scene }) {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!scene || !mountRef.current) return;
    const mount = mountRef.current;
    const w = mount.clientWidth || 800;
    const h = mount.clientHeight || 500;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setClearColor(scene.scene?.background || "#0E1116");
    mount.appendChild(renderer.domElement);

    const threeScene = new THREE.Scene();
    threeScene.background = new THREE.Color(scene.scene?.background || "#0E1116");

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 5000);
    const cam = scene.camera?.position || [50, 50, 50];
    camera.position.set(cam[0], cam[1], cam[2]);
    const tgt = scene.camera?.target || [0, 0, 0];
    camera.lookAt(tgt[0], tgt[1], tgt[2]);

    // Lights
    const amb = new THREE.AmbientLight(0xffffff, 0.7);
    threeScene.add(amb);
    const dir = new THREE.DirectionalLight(0xffffff, 0.8);
    dir.position.set(50, 100, 50);
    threeScene.add(dir);

    // Ground plane
    const g = scene.scene?.ground;
    if (g) {
      const geo = new THREE.PlaneGeometry(g.size[0], g.size[1]);
      const mat = new THREE.MeshLambertMaterial({
          color: g.color || "#1B0F08", side: THREE.DoubleSide });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.rotation.x = -Math.PI / 2;
      threeScene.add(mesh);
      // Grid overlay
      const grid = new THREE.GridHelper(
          Math.max(g.size[0], g.size[1]), 30, 0x374151, 0x1F2937);
      threeScene.add(grid);
    }

    // Meshes
    const labelGroup = new THREE.Group();
    (scene.meshes || []).forEach(m => {
      let geo, mesh;
      if (m.type === "box") {
        geo = new THREE.BoxGeometry(m.size[0], m.size[1], m.size[2]);
      } else if (m.type === "cylinder") {
        geo = new THREE.CylinderGeometry(m.size[0], m.size[0], m.size[1], 16);
      } else if (m.type === "plane") {
        geo = new THREE.PlaneGeometry(m.size[0], m.size[1]);
      } else {
        return;
      }
      const mat = new THREE.MeshLambertMaterial({
          color: m.color || "#888",
          transparent: m.opacity ? true : false,
          opacity: m.opacity ?? 1.0,
      });
      mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(m.position[0], m.position[1], m.position[2]);
      if (m.rotation) {
        mesh.rotation.set(m.rotation[0] || 0, m.rotation[1] || 0,
            m.rotation[2] || 0);
      }
      if (m.type === "plane") {
        mesh.rotation.x = -Math.PI / 2;
      }
      threeScene.add(mesh);
      // Edges (wireframe outline) for visual definition
      const edges = new THREE.EdgesGeometry(geo);
      const line = new THREE.LineSegments(edges,
          new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 1, transparent: true, opacity: 0.3 }));
      mesh.add(line);
    });
    threeScene.add(labelGroup);

    // Manual orbit controls (drag to rotate, wheel to zoom)
    let isDown = false, lastX = 0, lastY = 0;
    const target = new THREE.Vector3(tgt[0], tgt[1], tgt[2]);
    const sph = new THREE.Spherical().setFromVector3(camera.position.clone().sub(target));
    const onDown = (e) => { isDown = true; lastX = e.clientX; lastY = e.clientY; };
    const onUp = () => { isDown = false; };
    const onMove = (e) => {
      if (!isDown) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      sph.theta -= dx * 0.005;
      sph.phi -= dy * 0.005;
      sph.phi = Math.max(0.1, Math.min(Math.PI - 0.1, sph.phi));
      camera.position.copy(new THREE.Vector3().setFromSpherical(sph).add(target));
      camera.lookAt(target);
    };
    const onWheel = (e) => {
      e.preventDefault();
      sph.radius *= 1 + e.deltaY * 0.001;
      sph.radius = Math.max(5, Math.min(5000, sph.radius));
      camera.position.copy(new THREE.Vector3().setFromSpherical(sph).add(target));
      camera.lookAt(target);
    };
    renderer.domElement.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    renderer.domElement.addEventListener("mousemove", onMove);
    renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

    let frame;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      renderer.render(threeScene, camera);
    };
    animate();

    const onResize = () => {
      const w2 = mount.clientWidth, h2 = mount.clientHeight;
      camera.aspect = w2 / h2;
      camera.updateProjectionMatrix();
      renderer.setSize(w2, h2);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mouseup", onUp);
      renderer.domElement.removeEventListener("mousedown", onDown);
      renderer.domElement.removeEventListener("mousemove", onMove);
      renderer.domElement.removeEventListener("wheel", onWheel);
      renderer.dispose();
      try { mount.removeChild(renderer.domElement); } catch (_) { /* ok */ }
    };
  }, [scene]);

  return <div data-testid="gen-3d-viewer"
                  ref={mountRef} style={{ width: "100%", height: "100%",
                                                  minHeight: 400 }} />;
}
